import React from "react";
import { Col, Form, Image, Input, Modal, Row, Select, Upload } from "antd";
import { default as StyledSelect } from "components/Atoms/select";
import { CategorySelect } from "../../components/Molecules/select/category-select";
import { SubCategorySelect } from "../../components/Molecules/select/subcategory-select";
import { ProductGroupSelect } from "../../components/Molecules/select/product-group-select";
import { ExportMarketSelect } from "../../components/Molecules/select/export-market-select";
import {
  StaticDataContext,
  useStaticSupplierTags,
} from "context/static-data-context";
import { FormWithStepper } from "components/Organisms/formWithStepper";
import { FormInstance } from "antd/es/form/Form";
import { CloudUploadOutlined } from "@ant-design/icons";
import { useUploadFile } from "components/Organisms/uploads/api";
import Tag from "components/Atoms/tag";
import styled from "@emotion/styled";
import { withTranslation } from "react-i18next";
import { TOOLTIP } from "constants/tooltips";
import { InfoCircleOutlined } from "@ant-design/icons";

const Container = styled(Col)`
  height: 100%;
`;

const TooltipContent = styled.ul`
  list-style: disc;
  padding-left: 16px;
  padding-right: 16px;
  padding-top: 8px;
`;
export const CreateProductModal = withTranslation(["result"])((props: any) => {
  const {
    createProductSuccess,
    handleFormValuesChange,
    isCreating,
    categoryId,
    createProduct,
    createProductModalVisible,
    handleCloseModal,
    subCategoryId,
    t,
  } = props;
  const {
    ageGrade: ageGradeOptions,
    serviceRequired: ServiceRequiredOptions,
  } = React.useContext(StaticDataContext);
  const supplierTagsOptions = useStaticSupplierTags(true);
  const [createValues, setValues] = React.useState({});
  const { mutate: upload } = useUploadFile();

  const cusUpload = async (options: any) => {
    const { onSuccess, onError, file, onProgress } = options;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res: File = await upload(formData);
      onSuccess(res);
    } catch (err) {
      const error = new Error("Some error");
      onError({ err });
    }
  };

  const normFile = (e: any) => {
    if (e.file.status !== "done") {
      return e && e.fileList;
    }

    const temp = e.fileList.map((item: any) => {
      if (item.response) {
        return {
          uuid: item.response.uuid,
          docType: props.name,
          ...item,
        };
      }
      return item;
    });

    return temp;
  };

  const uploadProps = {
    name: "files",
    multiple: true,
    customRequest: cusUpload,
    accept: "image/*",
  };

  const [current, setCurrent] = React.useState(0);
  const createProductionRef = React.useRef<{ instanceForm: FormInstance }>();
  const getButtonByStep = () => {
    if (current < 1) return "Next";
    return "Create";
  };

  const onSubmitStep1 = (values: Record<string, any>) => {
    setValues({
      ...createValues,
      ...values,
      supplierTags: values.supplierTags.map(({ value }: any) => value),
    });
    setCurrent(current + 1);
  };
  const onSubmitStep2 = (values: Record<string, any>) => {
    setValues({
      ...createValues,
      productImages: values.productImages.map(({ response }: any) => ({
        ...response,
        type: "product_image",
      })),
    });
    if (createProductSuccess) {
      setCurrent(current + 1);
    }
  };

  const resetModal = () => {
    setCurrent(0);
    setValues({});
    createProductionRef.current?.instanceForm.resetFields();
  };

  React.useEffect(() => {
    if (createProductSuccess) {
      setCurrent(current + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createProductSuccess]);

  return (
    <Modal
      title={`Create a Product`}
      okText={createProductSuccess ? `Go to product` : getButtonByStep()}
      confirmLoading={isCreating}
      destroyOnClose
      onOk={async () => {
        try {
          const values = await createProductionRef.current?.instanceForm.validateFields();
          if (current < 1) {
            onSubmitStep1(values);
            return;
          }
          if (current < 2) {
            onSubmitStep2(values);
          }
          if (!createProductSuccess) {
            createProductionRef.current?.instanceForm.submit();
            return;
          }
          createProduct({ ...createValues });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn(new Error("Form has not valid"));
        }
      }}
      visible={createProductModalVisible}
      onCancel={() => {
        handleCloseModal();
        resetModal();
      }}
    >
      <FormWithStepper
        ref={createProductionRef}
        stepsProps={{
          current,
          onChange: (step) => {
            if (step >= 2) {
              setCurrent(createProductSuccess ? step : current);
            } else {
              setCurrent(step);
            }
          },
        }}
        steps={[
          {
            title: "Info",
            content: (
              <div>
                <div style={{ marginBottom: 15, color: "#4E5A71" }}>
                  Provide necessary information to create a product, you can
                  edit them later.
                </div>
                <Row justify={"space-between"} gutter={[8, 8]}>
                  <Col span={12}>
                    <Form.Item
                      name={"productName"}
                      label={"Product Name"}
                      rules={[
                        {
                          required: true,
                          message: "Product Name cannot be empty",
                        },
                      ]}
                    >
                      <Input placeholder={"Enter Product Name"} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name={"productNumber"}
                      label={"Product Number"}
                      rules={[
                        {
                          required: true,
                          message: "Product Number cannot be empty",
                        },
                      ]}
                    >
                      <Input placeholder={"Enter Product Number"} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row justify={"space-between"} gutter={[8, 8]}>
                  <Col span={12}>
                    <Form.Item
                      name={"categoryId"}
                      label={"Product Category"}
                      rules={[
                        {
                          required: true,
                          message: "Product Category cannot be empty",
                        },
                      ]}
                    >
                      <CategorySelect placeholder={"Enter Product Category"} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name={"subcategoryId"}
                      label={"Product Sub-category"}
                      rules={[
                        {
                          required: true,
                          message: "Product Sub-category cannot be empty",
                        },
                      ]}
                    >
                      <SubCategorySelect
                        categoryId={categoryId}
                        placeholder={"Enter Product Sub-category"}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row justify={"space-between"} gutter={[8, 8]}>
                  <Col span={12}>
                    <Form.Item
                      name={"productGroupId"}
                      label={"Product Group (if any)"}
                    >
                      <ProductGroupSelect
                        subCategoryId={subCategoryId}
                        placeholder={"Enter Product Group"}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name={"exportMarketId"}
                      label={"Export Market"}
                      rules={[
                        {
                          required: true,
                          message: "Export Market cannot be empty",
                        },
                      ]}
                    >
                      <ExportMarketSelect placeholder={"Enter Export Market"} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row justify={"space-between"} gutter={[8, 8]}>
                  <Col span={24}>
                    <Form.Item name={"supplierTags"} label={"Supplier Tag"}>
                      <StyledSelect
                        creatableProps={{
                          // value: createProductionRef.current?.instanceForm.getFieldValue(
                          //   "supplierTags"
                          // ),
                          onChange: (value: any) => {
                            createProductionRef.current?.instanceForm.setFields(
                              [
                                {
                                  name: "supplierTags",
                                  errors: [],
                                  value: value,
                                },
                              ]
                            );
                          },
                          isMulti: true,
                          components: {
                            MultiValue: (valueProps: any) => {
                              const { data, removeProps } = valueProps;
                              return (
                                <Tag closable onClose={removeProps.onClick}>
                                  {data?.label}
                                </Tag>
                              );
                            },
                          },
                        }}
                        isCreatable
                        options={(supplierTagsOptions || []) as any}
                      ></StyledSelect>
                    </Form.Item>
                  </Col>
                </Row>

                <Row justify={"space-between"} gutter={[8, 8]}>
                  <Col span={12}>
                    <Form.Item
                      name={"ageGrade"}
                      label={"Age Grade"}
                      rules={[
                        {
                          required: true,
                          message: "Estimate Completion Date cannot be empty",
                        },
                      ]}
                    >
                      <Select
                        options={ageGradeOptions}
                        placeholder="Select age grade"
                      ></Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      tooltip={{
                        title: (
                          <TooltipContent>
                            {TOOLTIP.serviceRequired.map((value, index) => {
                              return (
                                <li key={`service-required-${index}`}>
                                  {value}
                                </li>
                              );
                            })}
                          </TooltipContent>
                        ),
                        icon: <InfoCircleOutlined />,
                      }}
                      name={"serviceRequired"}
                      label={"Service Required"}
                      rules={[
                        {
                          required: true,
                          message: "Service Required cannot be empty",
                        },
                      ]}
                    >
                      <Select
                        options={ServiceRequiredOptions}
                        placeholder="Select service required"
                      ></Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row justify={"space-between"} gutter={[8, 8]}>
                  <Col span={24}>
                    <Form.Item
                      name={"productDescription"}
                      label={"Product Description"}
                      rules={[
                        {
                          required: true,
                          message: "Product description can not be empty",
                        },
                      ]}
                    >
                      <Input.TextArea
                        autoSize={{ minRows: 4 }}
                        placeholder="Enter product description"
                      ></Input.TextArea>
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            ),
          },
          {
            title: "Product Image",
            content: (
              <Row justify={"space-between"} gutter={[8, 8]}>
                <Container span={24}>
                  <Form.Item
                    name={"productImages"}
                    label={"Product Picture"}
                    valuePropName="fileList"
                    getValueFromEvent={normFile}
                    rules={[
                      {
                        required: true,
                        message: "Product Picture can not be empty",
                      },
                    ]}
                  >
                    <Upload.Dragger listType="picture-card" {...uploadProps}>
                      <p className="ant-upload-drag-icon">
                        <CloudUploadOutlined />
                      </p>
                      <p className="ant-upload-text">
                        Drop your files here, or Browse
                      </p>
                      <p className="ant-upload-hint">Support: JPG, JPEG, PNG</p>
                    </Upload.Dragger>
                  </Form.Item>
                </Container>
              </Row>
            ),
          },
          {
            title: "Done",
            content: (
              <div style={{ textAlign: "center" }}>
                <div> {t("result:createProductTitle")}</div>
                <div style={{ marginBottom: 15, color: "#4E5A71" }}>
                  {t("result:createProductSuccessMessage")}
                </div>
                <Image
                  preview={false}
                  style={{
                    margin: "20px auto",
                    width: "250px",
                    height: "100%",
                  }}
                  src={`/Product is created.png`}
                />
              </div>
            ),
          },
        ]}
        formProps={{
          name: "createProductionForm",
          onValuesChange: (changedValue) => {
            handleFormValuesChange(changedValue);
          },
          onFinish: () => {
            createProduct({ ...createValues });
          },
          layout: "vertical",
          initialValues: {
            serviceRequired: ServiceRequiredOptions[0].value,
            supplierTags: [],
          },
        }}
      ></FormWithStepper>
    </Modal>
  );
});
